import React, { useEffect, useRef } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { useCreateProjectMutation, useGetProjectsByCompanyQuery } from "gql/generated/types";
import { GET_PROJECTS_BY_COMPANY } from "gql/project.operations";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import { Controller, ControllerRenderProps, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { z } from "zod";
import useIsSsoLogin from "../../../../hooks/useIsSsoLogin";
import { apolloErrorToaster, getFormErrorMessage, successToaster } from "../utils/utils";

type ProjectFormValues = {
  title: string;
};

const schema = z.object({
  title: z.string().min(1, { message: T("word.required-fields") }),
});

const ProjectDropDown: React.FC = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProjectFormValues>({
    resolver: zodResolver(schema),
  });
  const ssoUsed = useIsSsoLogin();
  const { companyReference } = useCurrentCompanyContext()!;
  const { projectId } = useParams();
  const navigate = useNavigate();
  const op = useRef<OverlayPanel>(null);
  const { data } = useGetProjectsByCompanyQuery({
    variables: { refName: companyReference },
    skip: !companyReference || !ssoUsed,
  });

  useEffect(() => {
    if (data?.mdmProjectsByCompany.length === 0) {
      navigate("/mdm");
      return;
    }
    if (!projectId && data?.mdmProjectsByCompany && data?.mdmProjectsByCompany.length > 0) {
      navigate(`/mdm/${data?.mdmProjectsByCompany[0]?.id}/`);
    }
  }, [data, navigate, projectId]);

  const [createProjectMutation] = useCreateProjectMutation({
    onCompleted: () => successToaster(T("alert.project-created-successfully")),
    onError: apolloErrorToaster,
  });

  if(!ssoUsed) return <div>SSO login required</div>;

  const handleCreateProject: SubmitHandler<ProjectFormValues> = projectData => {
    createProjectMutation({
      variables: {
        project: { companyReference, title: projectData.title },
      },
      update: (cache, { data: qData }) => {
        cache.updateQuery({ query: GET_PROJECTS_BY_COMPANY, variables: {refName: companyReference}}, (oldData) => ({ 
          mdmProjectsByCompany: [...oldData.mdmProjectsByCompany, qData?.createMdmProject]
        }) );
      }
    });
    reset();
    op.current?.hide();
  };

  const handleChange = (e: DropdownChangeEvent ) => {
    navigate(`/mdm/${e.value}`)
  }

  const renderTitleControl = (field: ControllerRenderProps<ProjectFormValues, any>) => (
    <InputLabelWrapper labelName={T("word.project-name")} col={12}>
      <div className="p-inputgroup">
        <InputText
          value={field.value}
          placeholder={T("word.project-name")}
          onChange={e => field.onChange(e.target.value)}
        />
        <Button label={T("action.create")} className="p-button-success" type="submit" />
      </div>
      {getFormErrorMessage(field.name, errors)}
    </InputLabelWrapper>
  );

  return (
    <div className="p-inputgroup h-3rem w-15rem align-self-end">
      <Dropdown
        value={projectId ?? data?.mdmProjectsByCompany[0]?.id}
        options={data?.mdmProjectsByCompany || []}
        optionLabel="title"
        optionValue="id"
        onChange={handleChange}
        placeholder={T("word.select-one")}
        className="w-13rem"
      />
      <Button
        icon="pi pi-plus"
        title={T("action.create")}
        onClick={e => op.current?.toggle(e)}
        className="p-inputgroup-addon p-button p-button-success p-button-text bg-gray-200 border-gray-300"
      />
      <OverlayPanel ref={op}>
        <form onSubmit={handleSubmit(handleCreateProject)}>
          <Controller
            defaultValue=""
            name="title"
            control={control}
            render={({ field }) => renderTitleControl(field)}
          />
        </form>
      </OverlayPanel>
    </div>
  );
};

export default ProjectDropDown;
